import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Rocket, Plus, Trash2, ArrowRight, ArrowLeft, Check, Loader2, Sparkles, Users, Clock, Hash,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

const ROLES = ["editor", "designer", "seo", "smm", "manager", "viewer", "client"];
const TIMEZONES = [
  "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo",
  "UTC", "Europe/London", "Europe/Berlin", "Europe/Paris",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "Australia/Sydney",
];

export default function StudioQuickStart() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);

  // form state
  const [fullName, setFullName] = useState("");
  const [tz, setTz] = useState("Asia/Kolkata");
  const [title, setTitle] = useState("");
  const [handle, setHandle] = useState("");
  const [niche, setNiche] = useState("");
  const [team, setTeam] = useState([]);
  const [seedContent, setSeedContent] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/api/studio/quickstart/status`, { withCredentials: true })
      .then((r) => {
        setCompleted(!!r.data.completed);
        if (r.data.full_name) setFullName(r.data.full_name);
        if (r.data.timezone) setTz(r.data.timezone);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const addRow = () => setTeam((s) => [...s, { email: "", name: "", role: "editor" }]);
  const removeRow = (idx) => setTeam((s) => s.filter((_, i) => i !== idx));
  const updateRow = (idx, k, v) => setTeam((s) => s.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));

  const submit = async () => {
    if (!title.trim()) { toast.error("Channel/workspace title is required"); return; }
    setSubmitting(true);
    try {
      const cleanTeam = team
        .filter((t) => (t.email || "").trim())
        .map((t) => ({ email: t.email.trim(), name: t.name?.trim() || undefined, role: t.role || "editor" }));
      const r = await axios.post(
        `${API}/api/studio/quickstart/run`,
        {
          workspace_title: title.trim(),
          handle: handle.trim() || undefined,
          niche: niche.trim() || undefined,
          timezone: tz,
          full_name: fullName.trim() || undefined,
          team: cleanTeam,
          seed_sample_content: seedContent,
        },
        { withCredentials: true },
      );
      setResult(r.data);
      toast.success(`Workspace "${title}" created with ${cleanTeam.length} teammate(s)`);
      setStep(5);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick start failed");
    } finally { setSubmitting(false); }
  };

  if (loading) return null;

  return (
    <div data-testid="quickstart-page" style={{ background: "#F8FAFC", minHeight: "100vh", color: "#0F172A", padding: "60px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <span className="ai-seo-eyebrow"><Rocket size={14} /> 1-Click Quick Start</span>
        <h1 style={{ fontSize: 34, margin: "8px 0 4px", fontWeight: 800 }}>
          {completed ? "Add another workspace" : "Let's get you set up"}
        </h1>
        <p style={{ color: "#64748B", margin: "0 0 24px" }}>
          {step <= 4 && "Takes under 60 seconds. We'll create a channel workspace, invite your team and pre-fill a few sample tasks."}
        </p>

        {/* Stepper */}
        {step <= 4 && (
          <Stepper step={step} steps={["Profile", "Workspace", "Team", "Review"]} />
        )}

        {/* Step 1 – Profile */}
        {step === 1 && (
          <Card>
            <h2 style={{ marginTop: 0 }}>About you</h2>
            <Field label="Your full name (so teammates recognise you)">
              <input data-testid="qs-fullname" value={fullName} onChange={(e) => setFullName(e.target.value)} style={inputStyle} placeholder="Kamlesh Singad" />
            </Field>
            <Field label={<><Clock size={12} /> Time zone (drives daily reminders, reports)</>}>
              <select data-testid="qs-timezone" value={tz} onChange={(e) => setTz(e.target.value)} style={inputStyle}>
                {TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}
              </select>
            </Field>
            <NavBtns onNext={() => setStep(2)} canNext />
          </Card>
        )}

        {/* Step 2 – Workspace */}
        {step === 2 && (
          <Card>
            <h2 style={{ marginTop: 0 }}>Your workspace</h2>
            <Field label="Channel / workspace title *">
              <input data-testid="qs-title" value={title} onChange={(e) => setTitle(e.target.value)} style={inputStyle} placeholder="e.g. CodeWithKamlesh" />
            </Field>
            <Field label={<><Hash size={12} /> Handle (optional, used in URLs)</>}>
              <input data-testid="qs-handle" value={handle} onChange={(e) => setHandle(e.target.value)} style={inputStyle} placeholder="codewithkamlesh" />
            </Field>
            <Field label="Niche (optional)">
              <input data-testid="qs-niche" value={niche} onChange={(e) => setNiche(e.target.value)} style={inputStyle} placeholder="e.g. Coding tutorials, AI agencies, fitness creators" />
            </Field>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8, fontSize: 13, color: "#475569" }}>
              <input type="checkbox" data-testid="qs-seed" checked={seedContent} onChange={(e) => setSeedContent(e.target.checked)} />
              <Sparkles size={13} color="#a78bfa" /> Seed 3 sample content rows so the kanban isn't empty
            </label>
            <NavBtns onPrev={() => setStep(1)} onNext={() => setStep(3)} canNext={!!title.trim()} />
          </Card>
        )}

        {/* Step 3 – Team */}
        {step === 3 && (
          <Card>
            <h2 style={{ marginTop: 0 }}>Invite your team <Users size={16} style={{ verticalAlign: -2 }} /></h2>
            <p style={{ color: "#64748B", marginTop: -8 }}>Optional. You can do this later from the channel settings.</p>
            {team.length === 0 && (
              <div data-testid="qs-team-empty" style={{ padding: 14, textAlign: "center", color: "#64748B", border: "1px dashed #E2E8F0", borderRadius: 10, marginBottom: 12 }}>
                No teammates yet. Click <strong>"Add teammate"</strong> to invite by email.
              </div>
            )}
            {team.map((row, i) => (
              <div key={i} data-testid={`qs-team-row-${i}`} style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 0.7fr auto", gap: 8, marginBottom: 8 }}>
                <input
                  value={row.email}
                  onChange={(e) => updateRow(i, "email", e.target.value)}
                  placeholder="teammate@email.com"
                  data-testid={`qs-team-email-${i}`}
                  style={inputStyle}
                />
                <input
                  value={row.name}
                  onChange={(e) => updateRow(i, "name", e.target.value)}
                  placeholder="Name (optional)"
                  data-testid={`qs-team-name-${i}`}
                  style={inputStyle}
                />
                <select
                  value={row.role}
                  onChange={(e) => updateRow(i, "role", e.target.value)}
                  data-testid={`qs-team-role-${i}`}
                  style={inputStyle}
                >
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <button onClick={() => removeRow(i)} data-testid={`qs-team-remove-${i}`} style={{ background: "none", border: 0, color: "#ef4444", cursor: "pointer" }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button onClick={addRow} data-testid="qs-team-add" className="studio-btn-ghost" style={{ marginTop: 8, padding: "6px 12px", fontSize: 13 }}>
              <Plus size={12} /> Add teammate
            </button>
            <NavBtns onPrev={() => setStep(2)} onNext={() => setStep(4)} canNext />
          </Card>
        )}

        {/* Step 4 – Review */}
        {step === 4 && (
          <Card>
            <h2 style={{ marginTop: 0 }}>Review & launch</h2>
            <Row k="You" v={`${fullName || "—"} · ${tz}`} />
            <Row k="Workspace" v={title} />
            {handle && <Row k="Handle" v={`@${handle}`} />}
            {niche && <Row k="Niche" v={niche} />}
            <Row k="Team invites" v={`${team.filter((t) => t.email).length} teammate(s)`} />
            <Row k="Seed content" v={seedContent ? "3 sample rows" : "skip"} />
            <NavBtns
              onPrev={() => setStep(3)}
              onNext={submit}
              canNext={!submitting}
              nextLabel={submitting ? "Launching…" : "Launch workspace 🚀"}
              nextIcon={submitting ? <Loader2 size={14} className="animate-spin" /> : <Rocket size={14} />}
              nextTestId="qs-launch"
            />
          </Card>
        )}

        {/* Step 5 – Done */}
        {step === 5 && result && (
          <Card>
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(90deg, #a78bfa, #ec7c5e)", margin: "0 auto 14px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Check size={28} color="white" />
              </div>
              <h2 style={{ margin: "0 0 8px" }} data-testid="qs-done-title">You're all set ✨</h2>
              <p style={{ color: "#64748B" }}>
                <strong>{result.channel?.title}</strong> created · {result.invited?.length} teammate(s) invited · {result.seeded_content} sample rows.
              </p>
            </div>
            <button
              onClick={() => navigate(result.redirect_to)}
              className="studio-btn-primary"
              data-testid="qs-go-channel"
              style={{ width: "100%", padding: "12px", fontSize: 14, marginTop: 6 }}
            >
              Open my workspace <ArrowRight size={14} />
            </button>
          </Card>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px", borderRadius: 8,
  background: "#F8FAFC", border: "1px solid #E2E8F0",
  color: "#0F172A", fontSize: 14,
};

function Card({ children }) {
  return (
    <div style={{ marginTop: 16, padding: 22, borderRadius: 14, background: "#F8FAFC", border: "1px solid #E2E8F0" }}>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginBottom: 14 }}>
      <span style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, color: "#64748B", marginBottom: 6 }}>{label}</span>
      {children}
    </label>
  );
}

function Row({ k, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed #E2E8F0" }}>
      <span style={{ color: "#64748B" }}>{k}</span>
      <strong>{v}</strong>
    </div>
  );
}

function NavBtns({ onPrev, onNext, canNext, nextLabel = "Next", nextIcon, nextTestId }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 18, gap: 8 }}>
      {onPrev ? (
        <button onClick={onPrev} className="studio-btn-ghost" data-testid="qs-prev" style={{ padding: "10px 16px" }}>
          <ArrowLeft size={14} /> Back
        </button>
      ) : <span />}
      <button
        onClick={onNext}
        disabled={!canNext}
        className="studio-btn-primary"
        data-testid={nextTestId || "qs-next"}
        style={{ padding: "10px 18px", opacity: canNext ? 1 : 0.5 }}
      >
        {nextIcon || null} {nextLabel} {!nextIcon && <ArrowRight size={14} />}
      </button>
    </div>
  );
}

function Stepper({ step, steps }) {
  return (
    <div data-testid="qs-stepper" style={{ display: "flex", gap: 0, alignItems: "center", marginBottom: 12 }}>
      {steps.map((label, i) => {
        const idx = i + 1;
        const done = step > idx;
        const here = step === idx;
        return (
          <div key={label} style={{ flex: 1, display: "flex", alignItems: "center" }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: done ? "linear-gradient(90deg,#a78bfa,#ec7c5e)" : (here ? "rgba(167,139,250,0.2)" : "#E2E8F0"),
              border: `1px solid ${here ? "rgba(167,139,250,0.5)" : "#E2E8F0"}`,
              fontSize: 12, fontWeight: 700,
            }}>
              {done ? <Check size={12} /> : idx}
            </div>
            <span style={{ marginLeft: 8, fontSize: 12, color: here || done ? "#0F172A" : "#64748B" }}>{label}</span>
            {i < steps.length - 1 && (
              <div style={{ flex: 1, height: 2, marginLeft: 12, marginRight: 12, background: done ? "rgba(167,139,250,0.4)" : "#E2E8F0" }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
